/*
 * AudioWaveMaskCache
 * - Generates CSS mask images (as blob URLs) for audio waveform bars
 * - In-memory LRU cache (max 100 items)
 */

import { resampleVolumeProfile } from '@/modules/global/logic/audioVolumeAnalyzer'

// Geometry constants
const BAR_WIDTH = 3
const GAP_WIDTH = 2
const HEIGHT = 28
const BAR_RADIUS = 1
const MIN_BAR_HEIGHT_PX = 2
const MAX_BAR_HEIGHT_PX = 22

const CACHE_LIMIT = 100

class AudioWaveMaskCache {
  constructor() {
    /** @type {Map<string, { url: string }>} */
    this.cache = new Map()
  }

  /**
   * Compute the number of bars based on container width
   * @param {number} width
   */
  static computeTargetBarCount(width) {
    return Math.max(1, Math.floor((width + GAP_WIDTH) / (BAR_WIDTH + GAP_WIDTH)))
  }

  /**
   * Compute pixel heights for each bar, from normalized resampled volumes
   * @param {number[]} normalizedVolumes
   */
  static computePixelHeights(normalizedVolumes) {
    return normalizedVolumes.map((normalizedValue) => {
      const delta = (MAX_BAR_HEIGHT_PX - MIN_BAR_HEIGHT_PX) * normalizedValue
      return Math.round(MIN_BAR_HEIGHT_PX + delta)
    })
  }

  /**
   * Build a stable key for the cache
   * @param {Object} params
   * @param {number[]} params.pixelHeights
   * @param {number} params.width
   * @param {number} params.dpr
   */
  static buildKey({ pixelHeights, width, dpr }) {
    // We only need to vary by width, DPR and the actual pixel heights
    return [
      `w:${Math.floor(width)}`,
      `dpr:${Number(dpr || 1).toFixed(3)}`,
      `heights:${pixelHeights.join(',')}`,
    ].join('|')
  }

  /**
   * Move an entry to the most-recently-used position
   * @param {string} key
   */
  touch(key) {
    const entry = this.cache.get(key)
    if (!entry) {
      return
    }
    this.cache.delete(key)
    this.cache.set(key, entry)
  }

  /**
   * Evict the least-recently-used entry and revoke its URL.
   */
  evictIfNeeded() {
    if (this.cache.size < CACHE_LIMIT) {
      return
    }
    const oldestKey = this.cache.keys().next().value
    if (oldestKey) {
      const entry = this.cache.get(oldestKey)
      if (entry && entry.url) {
        URL.revokeObjectURL(entry.url)
      }
      this.cache.delete(oldestKey)
    }
  }

  /**
   * Generate the mask as a blob URL
   * @param {Object} params
   * @param {number[]} params.pixelHeights
   * @param {number} params.width
   * @param {number} params.dpr
   * @returns {Promise<string>} blob URL
   */
  static async drawMask({ pixelHeights, width, dpr }) {
    const componentWidth = Math.floor(width)
    const devicePixelRatio = Math.max(
      1,
      Number(
        dpr
        || (typeof window !== 'undefined' && window.devicePixelRatio)
        || 1,
      ),
    )

    const targetWidthPx = Math.floor(componentWidth * devicePixelRatio)
    const targetHeightPx = Math.floor(HEIGHT * devicePixelRatio)
    const canUseOffscreen = typeof OffscreenCanvas !== 'undefined'
      && typeof OffscreenCanvas.prototype !== 'undefined'
      && typeof OffscreenCanvas.prototype.convertToBlob === 'function'

    let canvas
    if (canUseOffscreen) {
      canvas = new OffscreenCanvas(targetWidthPx, targetHeightPx)
    } else {
      canvas = document.createElement('canvas')
      canvas.width = targetWidthPx
      canvas.height = targetHeightPx
    }

    const context2d = canvas.getContext('2d', { alpha: true, desynchronized: true })
    context2d.scale(devicePixelRatio, devicePixelRatio)

    context2d.clearRect(0, 0, componentWidth, HEIGHT)
    context2d.fillStyle = '#ffffff'

    let currentLeft = 0
    const barCount = pixelHeights.length
    for (let barIndex = 0; barIndex < barCount; barIndex++) {
      const barPixelHeight = Math.max(0, Math.min(HEIGHT, pixelHeights[barIndex] || 0))
      const top = Math.max(0, (HEIGHT - barPixelHeight) / 2)
      const left = Math.round(currentLeft)
      const right = left + BAR_WIDTH
      const bottom = top + barPixelHeight
      const cornerRadius = Math.max(0, Math.min(BAR_RADIUS, BAR_WIDTH / 2, barPixelHeight / 2))

      context2d.beginPath()
      if (cornerRadius > 0) {
        context2d.moveTo(left + cornerRadius, top)
        context2d.lineTo(right - cornerRadius, top)
        context2d.quadraticCurveTo(right, top, right, top + cornerRadius)
        context2d.lineTo(right, bottom - cornerRadius)
        context2d.quadraticCurveTo(right, bottom, right - cornerRadius, bottom)
        context2d.lineTo(left + cornerRadius, bottom)
        context2d.quadraticCurveTo(left, bottom, left, bottom - cornerRadius)
        context2d.lineTo(left, top + cornerRadius)
        context2d.quadraticCurveTo(left, top, left + cornerRadius, top)
      } else {
        context2d.rect(left, top, BAR_WIDTH, barPixelHeight)
      }
      context2d.closePath()
      context2d.fill()
      currentLeft += BAR_WIDTH + GAP_WIDTH
      if (currentLeft > componentWidth + 4) {
        break
      }
    }

    const blob = typeof canvas.convertToBlob === 'function'
      ? await canvas.convertToBlob({ type: 'image/png', quality: 0.9 })
      : await new Promise((resolve) => { canvas.toBlob(resolve, 'image/png', 0.9) })
    if (!blob) {
      throw new Error('Failed to generate mask blob')
    }
    return URL.createObjectURL(blob)
  }

  /**
   * Acquire a mask URL for given volumes/width/dpr, generating and caching it if necessary.
   *
   * @param {Object} params
   * @param {number[]} params.volumes - normalized volume profile (array of numbers in [0,1])
   * @param {number} params.width - component width in px
   * @param {number} [params.dpr] - device pixel ratio
   * @returns {Promise<string>} blob URL
   */
  async getAndCacheMask({ volumes, width, dpr }) {
    if (!Array.isArray(volumes) || volumes.length === 0) {
      return null
    }
    if (!width || width <= 0) {
      return null
    }

    const targetBarCount = AudioWaveMaskCache.computeTargetBarCount(width)
    const resampled = resampleVolumeProfile(volumes, targetBarCount)
    const pixelHeights = AudioWaveMaskCache.computePixelHeights(resampled)

    const key = AudioWaveMaskCache.buildKey({ pixelHeights, width, dpr })

    // Cache hit
    const existing = this.cache.get(key)
    if (existing) {
      this.touch(key)
      return existing.url
    }

    // Generate new
    const url = await AudioWaveMaskCache.drawMask({ pixelHeights, width, dpr })

    // Ensure capacity by evicting the oldest and revoking its URL
    this.evictIfNeeded()

    // Insert into cache
    this.cache.set(key, { url })
    this.touch(key)

    return url
  }
}

export const audioWaveMaskCache = new AudioWaveMaskCache()
